/**
 * **What happens when a session ends** — [ADR-0017](../../../../docs/adr/0017-a-revoked-session-ends-in-prodesk-too.md),
 * adopting FiveOX ADR-0079 and ADR-0041.
 *
 * An access token is a 15-minute RS256 JWT that every service verifies
 * *locally* against JWKS. Nothing asks whether the session behind it still
 * exists. So when somebody in the firm signs out of another FiveOX app —
 * which revokes the whole login group, ours included — **nothing this app can
 * see changes**: the stored token still verifies, every request still
 * succeeds, and a ProDesk tab that was closed at that moment reopens from
 * `localStorage` and carries on. That is the report the platform fixed this
 * against, verbatim: *"logout from one app, still logged in another"*.
 *
 * Three things have to be true, and this module is the one place that holds
 * all three:
 *
 * 1. **We ask.** `session.watch()` is the shell's watchdog (SESSION_WATCHDOG
 *    §3a): `GET /session/status` on start, on `focus`, on a bfcache restore
 *    and every 60s, coalesced to one call per 5s. A 401/403 ends the session;
 *    a 5xx, a timeout or a thrown fetch does **not** — auth being unavailable
 *    must never sign a practitioner out, which is the worse bug of the two.
 * 2. **We listen, scoped.** The ~2s sign-out broadcast has existed estate-wide
 *    since 2026-08-14 and this app never registered for it: there is no
 *    `onSignOut(...)` anywhere in `apps/web/src` before this file. It does now
 *    — with its own user id, so that another app tidying up somebody *else's*
 *    dead session does not sign this person out (`signout.ts`, module note).
 * 3. **We forget the firm.** See `clearsTheFirmOnEnd` below.
 *
 * The watchdog itself is emphatically **not** copied here.
 * [`lib/session.ts`](session.ts) is `createSessionStore()`, so `watch()` is
 * already a method on the store we use. The platform's own document files this
 * repo under §3b — *"your session module is your own"* — and tells it to
 * copy-adapt ~110 lines; **that is a misfiling**, recorded in ADR-0017 §1 and
 * owed back to the platform as PLATFORM_INTEGRATION §9.7. Copying those lines
 * would fork the policy of what a dead session means, which is exactly the
 * thing nine copies drift on, and constitution §1 forbids re-implementing a
 * core-app concern in the first place.
 */

import { onSignOut } from '@fiveox/shell'

import { setOrgId } from './api'
import { session } from './session'

/**
 * **The firm does not outlive the session that chose it** (ADR-0017 decision 4).
 *
 * The platform clears the *shared parent-domain* org cookie inside
 * `broadcastSignOut()`, which fixed this for the five core apps. **We are
 * outside that fix**, because ProDesk keeps its own per-app key
 * (`fiveox.prodesk_org`, `orgs.ts`) — deliberately, since storage is
 * per-origin and one app's selection must never be revived from another's.
 *
 * ProGST reached the same conclusion from a shorter argument: a stale key is a
 * cross-firm exposure. **That argument does not hold here unaltered**, and it
 * was checked rather than inherited. `resolveSelected()` already honours a
 * remembered org *only if the user is still a member of it*, so the next
 * person to sign in on this browser is never handed a firm they do not belong
 * to. Two things survive that check, and they are why this exists:
 *
 * - **The window before the orgs are fetched.** `api.ts` seeds its
 *   module-level `currentOrgId` from this key at import time, so every request
 *   the first paint makes carries `X-Fiveox-Org: <the previous person's
 *   firm>` under the new person's token. The service fails closed — a
 *   cross-tenant read is `NOT_FOUND`, never `FORBIDDEN` (constitution §3) — so
 *   nothing crosses. What is left behind is an audit trail: constitution §4
 *   logs every read, and a row attributing one to the wrong firm is exactly
 *   the evidence a partner is later asked to defend.
 * - **Two people who share a firm**, which the membership check waves through
 *   by design. A and B both belong to Kulkarni & Co and to a second practice;
 *   A leaves Kulkarni selected, B signs in and lands in Kulkarni rather than
 *   in B's own default org — because B *is* a member, `resolveSelected` is
 *   right to keep it, and the value it is keeping is A's. ADR-0002 makes the
 *   org the firm and this app ships an org switcher, so a user holding more
 *   than one practice is the ordinary case; a shared workstation in a practice
 *   is the ordinary case too.
 *
 * **On the transition, not on the state.** Clearing whenever the session is
 * merely *absent* would also fire during the SSO round trip, when `/callback`
 * renders with no session for a moment before it stores the tokens — and would
 * quietly delete the remembered firm on every single sign-in, which
 * `resolveSelected` exists to honour. So this fires only when a session that
 * **existed** has gone.
 *
 * **Through `setOrgId`, not `setSelectedOrgId`.** The key is only half of it:
 * `api.ts` also holds the org in a module-level variable that stamps the
 * header. Clearing the storage alone would leave this page's requests carrying
 * the old firm for the rest of its life.
 */
function clearsTheFirmOnEnd(): () => void {
  let had = session.getSession() !== null
  return session.subscribe(() => {
    const has = session.getSession() !== null
    if (had && !has) setOrgId(null)
    had = has
  })
}

/**
 * Arm all three. Mounted **once**, from the guard that already owns the
 * *no session → SSO* decision; the returned unsubscribe is what an effect's
 * cleanup wants.
 *
 * Nothing here navigates. `RequireSession` reads the session through
 * `useSyncExternalStore`, so ending it re-renders the app into `SsoRedirect`
 * on its own — and that is the authoritative path, because accounts, with a
 * live session, is what actually decides who this is.
 */
export function armSessionEnd(): () => void {
  const stopWatchdog = session.watch()
  const stopFirmClear = clearsTheFirmOnEnd()
  // `forUser` is read at the moment a broadcast arrives, never captured: a
  // refresh rotates the session and a captured id would be stale.
  const stopListener = onSignOut(
    () => session.clearSession(),
    () => session.getSession()?.userId ?? null,
  )
  return () => {
    stopWatchdog()
    stopFirmClear()
    stopListener()
  }
}
